import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { softDeleteProduct } from '@dukanti/shared';
import type { ProductWithImages } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { productKeys } from './use-products';

export interface DeleteProductMutationInput {
  productId: string;
  storeId: string;
}

interface DeleteProductContext {
  previousList: ProductWithImages[] | undefined;
}

/**
 * Soft-delete a product via the cascading `soft_delete_product` RPC
 * (design C5). The RPC removes storage objects + product_images rows
 * + flips `deleted_at` on the product, all in one SECURITY DEFINER
 * transaction.
 *
 * Optimistic UI: the list cache is filtered immediately via
 * `onMutate`, rolled back on error, and re-validated on settle. This
 * keeps the "click delete → row disappears" interaction instant.
 */
export function useDeleteProduct(): UseMutationResult<
  void,
  unknown,
  DeleteProductMutationInput,
  DeleteProductContext
> {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, DeleteProductMutationInput, DeleteProductContext>({
    mutationFn: async ({ productId }) => {
      await softDeleteProduct(supabase, productId);
    },
    onMutate: async ({ productId, storeId }) => {
      const listKey = productKeys.list(storeId);
      await queryClient.cancelQueries({ queryKey: listKey });
      const previousList = queryClient.getQueryData<ProductWithImages[]>(listKey);
      if (previousList) {
        queryClient.setQueryData<ProductWithImages[]>(
          listKey,
          previousList.filter((p) => p.id !== productId),
        );
      }
      return { previousList };
    },
    onError: (_err, variables, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(productKeys.list(variables.storeId), context.previousList);
      }
    },
    onSettled: (_data, _err, variables) => {
      void queryClient.invalidateQueries({
        queryKey: productKeys.list(variables.storeId),
      });
      queryClient.removeQueries({
        queryKey: productKeys.detail(variables.productId),
      });
    },
  });
}
